import { css, unsafeCSS } from "lit";
export const lensVar = (name) => css `var(--lens-${unsafeCSS(name)})`;
export const coreVar = (name) => lensVar(`core-${name}`);
export const semanticVar = (name) => lensVar(`semantic-${name}`);
export const componentVar = (name) => lensVar(`component-${name}`);
export const headerVar = (name) => componentVar(`global-header-${name}`);
export const navVar = (name) => componentVar(`global-nav-${name}`);
export const bodyStyles = css `
  :root {
    --atlas-side-nav-width: ${navVar("expanded-width")};
    --atlas-side-nav-collapse-width: ${navVar("collapsed-width")};
    --atlas-global-header-height: ${headerVar("height")};
    --atlas-global-nav-animation-duration: 0.2s;
    --atlas-global-nav-content-left: 0px;
    --atlas-global-nav-easing: cubic-bezier(0.33, 1, 0.68, 1);
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    :root {
      --atlas-global-nav-animation-duration: 0s;
    }
  }

  body.atlas-gn-panel--open {
    --atlas-global-nav-content-left: var(--atlas-side-nav-width);
  }
  body.atlas-gn-panel--collapsed {
    --atlas-global-nav-content-left: var(--atlas-side-nav-collapse-width);
  }
  body.atlas-gn-shortcuts--open {
    overflow: hidden;
  }

  [data-atlas-gn-app] {
    margin-left: var(--atlas-global-nav-content-left);
    transition-property: margin-left;
    transition-duration: var(--atlas-global-nav-animation-duration);
    transition-timing-function: var(--atlas-global-nav-easing);
  }
`;
const buttonVar = (name) => componentVar(`button-${name}`);
const mediumButtonVar = (name) => buttonVar(`medium-${name}`);
export const iconButtonVar = (name) => buttonVar(`icon-only-${name}`);
const secondaryButtonVar = (name) => buttonVar(`secondary-${name}`);
const tertiaryButtonVar = (name) => buttonVar(`tertiary-${name}`);
export const buttonDefaults = css `
  button {
    display: flex;
    flex-direction: row;
    text-align: left;
    align-items: center;
    font: inherit;
    box-sizing: border-box;
    border: var(--button-border, none);
    color: var(--button-color, inherit);
    background: var(--button-background, none);
    -webkit-tap-highlight-color: var(--button-hover-background, none);
    transition-property: background-color, color, border-color;
    transition-duration: var(--atlas-global-nav-animation-duration);
    transition-timing-function: var(--atlas-global-nav-easing);
  }
  @media (hover) {
    button:not(:disabled) {
      cursor: pointer;
    }
    button:not(:disabled):hover {
      --button-background: var(--button-hover-background, none);
      --button-color: var(--button-hover-color, inherit);
    }
    button:not(:disabled):focus {
      outline-style: solid;
      outline-width: ${semanticVar("border-width-focus-width")};
      outline-color: ${semanticVar("color-ui-focus-ring")};
      outline-offset: ${semanticVar("border-width-focus-offset")};
    }
    button:not(:disabled):active {
      --button-background: var(--button-active-background, none);
      --button-color: var(--button-active-color, inherit);
    }
  }
`;
export const mediumButton = css `
  button.medium {
    height: ${mediumButtonVar("height")};
    border-radius: ${mediumButtonVar("border-radius")};
    padding-left: ${mediumButtonVar("padding-left")};
    padding-right: ${mediumButtonVar("padding-right")};
    padding-top: ${mediumButtonVar("padding-top")};
    padding-bottom: ${mediumButtonVar("padding-bottom")};
    gap: ${mediumButtonVar("gap")};
    font-size: ${mediumButtonVar("font-size")};
    font-weight: ${mediumButtonVar("font-weight")};
    line-height: ${mediumButtonVar("line-height")};
  }
`;
export const iconButton = css `
  button.icon-button {
    justify-content: center;
    border: var(--button-border, none);
    height: ${iconButtonVar("medium-width")};
    width: ${iconButtonVar("medium-width")};
    padding: ${iconButtonVar("medium-padding-left")};
  }
`;
export const secondaryButton = css `
  button.secondary {
    --button-background: ${secondaryButtonVar("default-background")};
    --button-border: ${secondaryButtonVar("default-border-width")}
      ${secondaryButtonVar("default-border-style")} ${secondaryButtonVar("default-border-color")};
    --button-hover-background: ${tertiaryButtonVar("hover-background")};
    --button-hover-color: ${secondaryButtonVar("hover-color")};
    --button-active-background: ${tertiaryButtonVar("active-background")};
    --button-active-color: ${secondaryButtonVar("active-color")};
  }
`;
export const tertiaryButton = css `
  button.tertiary {
    --button-background: ${tertiaryButtonVar("default-background")};
    --button-color: ${tertiaryButtonVar("default-color")};
    --button-hover-background: ${tertiaryButtonVar("hover-background")};
    --button-hover-color: ${tertiaryButtonVar("hover-color")};
    --button-active-background: ${tertiaryButtonVar("active-background")};
    --button-active-color: ${tertiaryButtonVar("active-color")};
  }
`;
export const statusIndicator = css `
  .status-indicator {
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    padding-top: ${coreVar("spacing-0-5")};
    padding-bottom: ${coreVar("spacing-0-5")};
    padding-left: ${coreVar("spacing-1-5")};
    padding-right: ${coreVar("spacing-1-5")};
    font-size: ${semanticVar("font-label-sm-font-size")};
    line-height: ${semanticVar("font-label-sm-line-height")};
    letter-spacing: ${semanticVar("font-label-sm-letter-spacing")};
    font-weight: ${semanticVar("font-weight-emphasis")};
    height: ${coreVar("spacing-3")};
    border-radius: ${semanticVar("radius-lg")};
    background-color: ${semanticVar("color-accent-yellow-background")};
  }
`;
export const transition = css `
  .transition {
    transition-duration: var(--atlas-global-nav-animation-duration);
    transition-timing-function: var(--atlas-global-nav-easing);
  }
`;
export const srOnly = css `
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;
const listVar = (name) => navVar(`list-${name}`);
const listItemVar = (name) => navVar(`list-item-${name}`);
export const appSwitcherItem = css `
  .item {
    display: flex;
    font: inherit;
    box-sizing: border-box;
    align-items: center;
    border: none;
    text-align: left;
    user-select: none;
    text-decoration: none;
    background-color: ${listItemVar("default-background-color")};
    color: ${listItemVar("default-color")};
    font-size: ${listItemVar("font-size")};
    line-height: ${listItemVar("line-height")};
    font-weight: ${listItemVar("font-weight")};
    letter-spacing: ${listItemVar("letter-spacing")};
    padding: ${listItemVar("padding")};
    gap: ${listItemVar("gap")};
    border-radius: ${listItemVar("border-radius")};
    -webkit-tap-highlight-color: ${listItemVar("hover-background-color")};
    transition-property: background-color, color, border-color;
  }

  ::slotted(a) {
    border-radius: ${listItemVar("border-radius")};
    text-decoration: none;
  }

  .item span.label {
    display: var(--label-display, inline-flex);
    flex-grow: 1;
  }
  .item > svg,
  .item > .icon {
    display: inline-flex;
    width: ${semanticVar("icon-size-lg")};
  }
  .item > svg ~ svg,
  .item > .icon ~ .icon {
    display: var(--label-display, inline-flex);
  }

  .item.disabled {
    color: ${semanticVar("color-type-disabled")};
  }
  .item.highlighted {
    background: ${listItemVar("open-default-background-color")};
  }

  @media (hover) {
    .item:not(.disabled) {
      cursor: pointer;
    }
    .item:not(.disabled):hover {
      background-color: ${listItemVar("hover-background-color")};
    }
    ::slotted(a:focus),
    ::slotted(a:focus-visible),
    .item:not(.disabled):focus,
    .item:not(.disabled):focus-visible {
      outline-style: solid;
      outline-width: ${semanticVar("border-width-focus-width")};
      outline-color: ${coreVar("color-gray-0")};
      outline-offset: ${semanticVar("border-width-focus-offset")};
    }
    .item.highlighted:not(:disabled):hover {
      background: ${listItemVar("open-hover-background-color")};
      box-shadow: ${listItemVar("open-hover-box-shadow")};
    }
  }
  .item dil-expand-right-icon {
    display: var(--label-display, flex);
  }

  .item.current {
    background: ${listItemVar("current-background-color")};
    color: ${listItemVar("current-color")};
    &:not(:disabled):hover {
      background: ${listItemVar("current-background-color")};
    }
  }

  details {
    display: flex;
    flex-direction: column;
    list-style: none;
    gap: ${listVar("gap")};
  }
  details > summary::-webkit-details-marker {
    display: none;
  }
  details > ul {
    padding-top: ${listVar("padding-top")};
  }
  ul {
    display: flex;
    flex-direction: column;
    margin: 0;
    list-style: none;
    gap: ${navVar("list-gap")};
    padding: 0;
  }
`;
